<script setup lang="ts">
/**
 * The set's outline: one row per exercise, labelled by position with its type and a prompt snippet.
 * Add picks the type up front; reorder and remove sit on the row.
 */
import type { NativeSelectOption } from '@shared/ui/fields'

import { faArrowDown, faArrowUp, faTrashCan } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { NativeSelect } from '@shared/ui/fields'
import { BtnIcon, BtnSecondary } from '@shared/ui/buttons'

import { useMathExerciseList } from '../../../../model/assignments/math-exercise-list.hook'

defineOptions({
  name: 'ExerciseList',
})

const {
  refExercises,
  refHasExercises,
  refNewType,
  refNewTypeOptions,
  setNewType,
  selectExercise,
  addExercise,
  removeExercise,
  moveExerciseUp,
  moveExerciseDown,
} = useMathExerciseList()
</script>

<template>
  <div class="exercise-list">
    <AppText name="body1-medium">
      Exercises
    </AppText>

    <AppText
      v-if="!refHasExercises"
      name="body2"
      color="slate-500"
    >
      No exercises yet. Add the first one below.
    </AppText>

    <ul class="exercise-list__items">
      <li
        v-for="exercise in refExercises"
        :key="exercise.id || `exercise-${exercise.position}`"
        :class="['exercise-list__item', { 'is-selected': exercise.isSelected }]"
      >
        <button
          type="button"
          class="exercise-list__select"
          @click="selectExercise(exercise.id)"
        >
          <AppText
            name="body2-medium"
            truncate
          >
            {{ exercise.label }} · {{ exercise.typeLabel }}
          </AppText>
          <AppText
            name="body2"
            color="slate-500"
            truncate
          >
            {{ exercise.summary }}
          </AppText>
        </button>
        <div class="exercise-list__actions">
          <BtnIcon
            :icon="faArrowUp"
            size="small"
            title="Move exercise up"
            :disabled="exercise.isFirstInOrder"
            @click="moveExerciseUp(exercise.id)"
          />
          <BtnIcon
            :icon="faArrowDown"
            size="small"
            title="Move exercise down"
            :disabled="exercise.isLastInOrder"
            @click="moveExerciseDown(exercise.id)"
          />
          <BtnIcon
            :icon="faTrashCan"
            size="small"
            color="red"
            title="Remove exercise"
            @click="removeExercise(exercise.id)"
          />
        </div>
      </li>
    </ul>

    <div class="exercise-list__add">
      <NativeSelect
        name="new-exercise-type"
        label="New exercise type"
        :value="refNewType"
        :options="refNewTypeOptions"
        @change="(option: NativeSelectOption) => setNewType(String(option.value))"
      />
      <BtnSecondary
        text="Add exercise"
        element-width="full"
        @click="addExercise"
      />
    </div>
  </div>
</template>

<style scoped lang="scss" src="./ExerciseList.scss" />
